import React from 'react';
import { ModalBase } from './ModalBase';
import { Multometro } from './Multometro';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const successStories = [
  {
    country: `Francia`,
    flag: '🇫🇷',
    fineType: `Exceso de Velocidad`,
    result: 'ANULADA',
    resultColor: 'bg-green-500',
    quote: `Recibí una multa de un radar en Francia y no sabía qué hacer. Multas.EU se encargó de todo y consiguieron anularla por un defecto en la notificación. ¡Servicio impecable!`,
  },
  {
    country: `España`,
    flag: '🇪🇸',
    fineType: `Aparcamiento en zona SER`,
    result: 'ANULADA',
    resultColor: 'bg-green-500',
    quote: `Me multaron en Madrid supuestamente por no tener ticket. Envié mi caso, demostraron que la señalización era confusa y ganaron el recurso. ¡Me ahorraron 90€!`,
  },
  {
    country: `Italia`,
    flag: '🇮🇹',
    fineType: `Acceso a Zona de Tráfico Limitado (ZTL)`,
    result: 'REDUCIDA 50%',
    resultColor: 'bg-yellow-500',
    quote: `Entré en una ZTL en Florencia sin darme cuenta. La multa era altísima. El equipo legal de Multas.EU consiguió una reducción del 50% alegando falta de señalización internacional.`,
  },
   {
    country: `Alemania`,
    flag: '🇩🇪',
    fineType: `Distancia de seguridad`,
    result: 'ANULADA',
    resultColor: 'bg-green-500',
    quote: `Me acusaron de no mantener la distancia de seguridad en la Autobahn. Parecía imposible de recurrir, pero sus expertos encontraron un error en la medición del sistema policial. Increíble.`,
  },
];

const SuccessStoryCard: React.FC<typeof successStories[0]> = ({ country, flag, fineType, result, resultColor, quote }) => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden transform transition-transform duration-300 hover:scale-105 hover:shadow-xl border border-gray-200">
        <div className="p-6">
            <div className="flex justify-between items-start">
                <div>
                    <div className="flex items-center text-lg font-bold text-gray-800">
                        <span className="text-2xl mr-2">{flag}</span>
                        {country}
                    </div>
                    <p className="text-sm text-gray-500">{fineType}</p>
                </div>
                <div className={`px-3 py-1 text-sm font-bold text-white ${resultColor} rounded-full shadow`}>
                    {result}
                </div>
            </div>
            <blockquote className="mt-4 border-l-4 border-gray-200 pl-4">
                <p className="text-gray-600 italic">"{quote}"</p>
            </blockquote>
        </div>
    </div>
);

export const SuccessStoriesModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  return (
    <ModalBase
        isOpen={isOpen}
        onClose={onClose}
        title="Casos de Éxito Reales"
        containerClassName="max-w-4xl"
        mainClassName="bg-gray-50"
    >
       <div className="text-center mb-2">
            <h3 className="text-3xl font-bold text-gray-800">La prueba de nuestro trabajo son los resultados</h3>
            <p className="mt-3 text-lg text-gray-600">
                Tu confianza es nuestro mayor activo. Nuestro "Multómetro" refleja nuestra alta tasa de éxito.
            </p>
        </div>

        <Multometro />

        <div className="my-8 p-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg shadow-md">
            <div className="flex items-center mb-3">
                <span className="material-icons-outlined text-3xl text-blue-600 mr-3">verified_user</span>
                <h4 className="text-xl font-bold text-blue-800">Victoria Legal Histórica: ZBE Anulada</h4>
            </div>
            <p className="text-gray-700 mb-4">
                Uno de nuestros logros más significativos ha sido la anulación de la Zona de Bajas Emisiones (ZBE) del Ayuntamiento de Barcelona. Nuestro servicio jurídico lideró una batalla legal que culminó en sentencias favorables, sentando un precedente crucial para los derechos de los conductores en toda España.
            </p>
            <a
                href="https://blog.multas.org/archivos/73"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:underline inline-flex items-center"
            >
                Leer: "Las Sentencias que Tumbaron la ZBE del Ayuntamiento de Barcelona: Victoria Legal Histórica de Nuestro Servicio Jurídico"
                <span className="material-icons-outlined text-base ml-1">open_in_new</span>
            </a>
        </div>

        <div className="my-8 p-6 bg-green-50 border-l-4 border-green-500 rounded-r-lg shadow-md">
            <div className="flex items-center mb-3">
                <span className="material-icons-outlined text-3xl text-green-600 mr-3">copyright</span>
                <h4 className="text-xl font-bold text-green-800">La Victoria Sobre el Canon Digital</h4>
            </div>
            <p className="text-gray-700 mb-4">
                Lideramos una de las primeras y más importantes batallas legales contra el injusto 'canon digital' impuesto por la SGAE. Nuestra argumentación demostró que la tasa indiscriminada sobre dispositivos de almacenamiento era ilegal, sentando un precedente fundamental en la defensa de los derechos de los consumidores y empresas frente a tasas abusivas.
            </p>
            <a
                href="https://blog.multas.org/archivos/144"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-green-600 hover:underline inline-flex items-center"
            >
                Leer: "La Victoria Sobre el Canon Digital"
                <span className="material-icons-outlined text-base ml-1">open_in_new</span>
            </a>
        </div>
        
        <div className="text-center mb-10 mt-4">
            <p className="text-lg text-gray-600">
                Estos son algunos ejemplos de cómo hemos ayudado a conductores como tú.
            </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
            {successStories.map((story, index) => (
                <SuccessStoryCard key={index} {...story} />
            ))}
        </div>
    </ModalBase>
  );
};